import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageTemplate from '../components/PageTemplate';
import { fetchRecipeIndex } from '../utils/recipeFetcher';
import { validateRecipeIndex } from '../utils/indexValidator';
import { truncateDescription } from '../utils/recipeParser';
import '../styles/recipes.css';

/**
 * RecipeBrowserPage displays all recipe categories and recipe cards.
 * Fetches the recipe index at runtime from /recipes/index.json.
 * Uses PageTemplate with title="Family Recipes" and icon="fas fa-utensils".
 * Route: /recipes
 */
const RecipeBrowserPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRecipeIndex()
      .then(data => {
        const validated = validateRecipeIndex(data);
        setCategories(validated.categories);
      })
      .catch(err => setError(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <PageTemplate title="Family Recipes" icon="fas fa-utensils">
        <div className="section">
          <div className="recipe-loading-state" role="status" aria-label="Loading recipes">
            <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
            <p>Loading recipes...</p>
          </div>
        </div>
      </PageTemplate>
    );
  }

  if (error) {
    return (
      <PageTemplate title="Family Recipes" icon="fas fa-utensils">
        <div className="section">
          <div className="recipe-error-state">
            <h2>Unable to Load Recipes</h2>
            <p>{error.message || 'An unexpected error occurred while loading recipes.'}</p>
            <Link to="/recipes">Back to Recipes</Link>
          </div>
        </div>
      </PageTemplate>
    );
  }

  if (categories.length === 0) {
    return (
      <PageTemplate title="Family Recipes" icon="fas fa-utensils">
        <div className="section">
          <p className="empty-state-message">No recipes available yet</p>
        </div>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate title="Family Recipes" icon="fas fa-utensils">
      <div className="section recipe-detail-section">
        {categories.map((category) => (
          <div key={category.name} className="category-section">
            <h2 className="category-heading">{category.displayName}</h2>
            {category.description && (
              <p className="category-description">{category.description}</p>
            )}
            <div className="card-grid">
              {category.recipes.map((recipe) => (
                <Link
                  key={recipe.slug}
                  to={`/recipes/${recipe.category}/${recipe.slug}`}
                  className="card-link"
                >
                  <div className="card">
                    <h3 className="card-title">{recipe.title}</h3>
                    <p className="card-description">
                      {truncateDescription(recipe.description, 150)}
                    </p>
                    <div className="card-meta">
                      <span className={`badge badge-${recipe.difficulty}`}>
                        {recipe.difficulty}
                      </span>
                      <span className="card-time">
                        <i className="fas fa-clock" aria-hidden="true"></i>
                        {recipe.prepTime} + {recipe.cookTime}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </PageTemplate>
  );
};

export default RecipeBrowserPage;
